import React, { useCallback, useEffect, useRef} from "react";
import { DataGrid} from "@mui/x-data-grid";
import { Box } from "@mui/material";
import { columns } from "./AuditLogDataTable.config";
import throttle from "lodash/throttle";
import { AuditLogsDataType } from "../../../interface";

interface AuditLogsDataTableProps {
  rows?: AuditLogsDataType[];
  isLoading?: boolean;
  totalRecord?: number;
  limit: number[];
  nextKey: string;
  prevKey: string;
  updateLimit?: (value: number[]) => void;
  updateKey?: (value: string) => void;
}

const AuditLogsDataTable: React.FC<AuditLogsDataTableProps> = ({
  rows = [],
  isLoading = false,
  totalRecord = 0,
  updateLimit,
  limit,
}) => {

  const fetchMoreData = useCallback(() => {
    if (rows.length < totalRecord) {
      const [startLmit, endLimit] = limit;
      if (startLmit + 50 < totalRecord) {
        updateLimit?.([startLmit + 50, endLimit + 50]);
      }
    }
  }, [rows, totalRecord, limit, updateLimit]);
  const lastScrollTopRef = useRef(0);
  const lastScrollLeftRef = useRef(0);

  useEffect(() => {
    let container: HTMLElement | null = null;
    let rafId: number | null = null;

    const handleScroll = throttle(
      (event: Event) => {
        const target = event.target as HTMLElement;
        const currentTop = target.scrollTop;
        const currentLeft = target.scrollLeft;

        if (currentLeft !== lastScrollLeftRef.current) {
          lastScrollLeftRef.current = currentLeft;
          return;
        }

        if (currentTop <= lastScrollTopRef.current) {
          lastScrollTopRef.current = currentTop;
          return;
        }

        lastScrollTopRef.current = currentTop;

        const distanceToBottom =
          target.scrollHeight - target.clientHeight - target.scrollTop;

        if (distanceToBottom <= 50) {
          fetchMoreData();
        }
      },
      150,
      { leading: true, trailing: true }
    );

    const attach = () => {
      container =
        document.querySelector<HTMLElement>(".MuiDataGrid-virtualScroller") ||
        document.querySelector<HTMLElement>(".MuiDataGrid-virtualScrollerContent");

      if (!container) {
        rafId = requestAnimationFrame(attach);
        return;
      }
      container.addEventListener("scroll", handleScroll, { passive: true });
    };

    attach();

    return () => {
      if (container) container.removeEventListener("scroll", handleScroll);
      handleScroll.cancel?.();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [fetchMoreData]);

  return (
    <Box className="grid-container">
      <DataGrid
        loading={isLoading}
        rows={rows.map((item, index) => ({ idn: index + 1, ...item }))}
        columns={columns}
        disableRowSelectionOnClick
        disableColumnMenu
        rowBufferPx={500}
        columnBufferPx={150}
        paginationMode="server"
        rowCount={totalRecord}
        localeText={{
          noRowsLabel: "No records found",
        }}
        getRowId={(row) => row?.audit_id}
        hideFooter
      />
    </Box>
  );
};

export default AuditLogsDataTable;
